import { useEffect, useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import type { Bond } from '../api';
import { corpoDoErro, mensagemDeErro, statusDoErro } from '../api';
import type { CollaboratorDocument, DocumentTypeItem, UploadPayload } from '../api/documentos';
import { DOCUMENT_CATEGORY_LABELS } from './format';
import { PessoasModal } from './PessoasModal';

const BOND_CATEGORIES = new Set(['trabalhista', 'funcional', 'afastamento', 'desligamento']);

interface Props {
  open: boolean;
  /** `dp` mostra vínculo, origem e datas; `me` é o envio pelo próprio colaborador. */
  mode: 'dp' | 'me';
  types: DocumentTypeItem[];
  bonds?: Bond[];
  /** Nova versão de um documento: o tipo fica fixo. */
  previous?: CollaboratorDocument | null;
  /** Tipo pré-selecionado (ex.: item do checklist). */
  presetTypeId?: string | null;
  submitting: boolean;
  onSubmit: (payload: UploadPayload) => Promise<unknown>;
  onClose: () => void;
}

/**
 * Inclusão de documento ou de nova versão. Formatos e tamanho vêm do tipo;
 * arquivo idêntico já incluído pede confirmação (RN-GP-002-019).
 */
export function UploadDocumentModal({ open, mode, types, bonds = [], previous, presetTypeId, submitting, onSubmit, onClose }: Props) {
  const [typeId, setTypeId] = useState('');
  const [bondId, setBondId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [number, setNumber] = useState('');
  const [origin, setOrigin] = useState<UploadPayload['origin']>('manual');
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTypeId(previous?.documentTypeId ?? presetTypeId ?? '');
    setBondId(previous?.employmentBondId ?? bonds.find((b) => !b.closed)?.id ?? '');
    setFile(null);
    setIssuedAt('');
    setExpiresAt('');
    setNumber('');
    setOrigin('manual');
    setError(null);
    setDuplicate(false);
  }, [open, previous, presetTypeId, bonds]);

  const type = useMemo(() => types.find((t) => t.id === typeId), [types, typeId]);
  const accept = type ? type.allowedKinds.map((k) => (k === 'jpeg' ? '.jpg,.jpeg' : `.${k}`)).join(',') : '.pdf,.png,.jpg,.jpeg';
  const needsBond = !!type && BOND_CATEGORIES.has(type.category);

  async function submit(confirmDuplicate = false) {
    if (!typeId) return setError('Selecione o tipo de documento.');
    if (!file) return setError('Selecione o arquivo.');
    if (type && file.size > type.maxSizeMb * 1024 * 1024) return setError(`O arquivo excede ${type.maxSizeMb} MB.`);
    if (mode === 'dp' && needsBond && !bondId) return setError('Selecione o vínculo do documento.');
    setError(null);
    try {
      await onSubmit({
        documentTypeId: typeId,
        file,
        previousVersionId: previous?.id,
        employmentBondId: mode === 'dp' && bondId ? bondId : undefined,
        issuedAt: issuedAt || undefined,
        expiresAt: expiresAt || undefined,
        documentNumber: number || undefined,
        origin: mode === 'dp' ? origin : undefined,
        allowDuplicate: confirmDuplicate || undefined,
      });
    } catch (err) {
      if (statusDoErro(err) === 409 && (corpoDoErro(err) as { duplicateOf?: string }).duplicateOf) {
        setDuplicate(true);
        return setError('Este arquivo já foi enviado para este tipo de documento. Confirme para incluir mesmo assim.');
      }
      setError(mensagemDeErro(err, 'Não foi possível enviar o documento.'));
    }
  }

  return (
    <PessoasModal
      open={open}
      size="lg"
      title={previous ? `Nova versão — ${previous.typeName}` : 'Enviar documento'}
      description={type ? `Formatos: ${type.allowedKinds.map((k) => k.toUpperCase()).join(', ')} · até ${type.maxSizeMb} MB` : undefined}
      submitLabel={duplicate ? 'Incluir mesmo assim' : 'Enviar'}
      isSubmitting={submitting}
      submitError={error}
      onClose={onClose}
      onSubmit={() => submit(duplicate)}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect
          label="Tipo de documento"
          disabled={!!previous}
          options={types.map((t) => ({ value: t.id, label: t.name, description: DOCUMENT_CATEGORY_LABELS[t.category] }))}
          value={typeId}
          onChange={(v) => { setTypeId(v); setDuplicate(false); }}
        />
        {mode === 'dp' && needsBond && (
          <AutocompleteSelect
            label="Vínculo"
            options={bonds.map((b) => ({ value: b.id, label: `Matrícula ${b.internalRegistration} · ${b.statusLabel}` }))}
            value={bondId}
            onChange={setBondId}
          />
        )}
        <TextInput label="Número do documento (opcional)" value={number} onChange={(e) => setNumber(e.target.value)} />
        <TextInput label="Data de emissão" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
        {type?.hasExpiry && (
          <TextInput
            label={type.defaultValidityMonths ? `Validade (padrão: ${type.defaultValidityMonths} meses da emissão)` : 'Validade'}
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        )}
        {mode === 'dp' && (
          <AutocompleteSelect
            label="Origem"
            options={[
              { value: 'manual', label: 'Inclusão pelo DP/RH' },
              { value: 'email', label: 'Recebido por e-mail' },
              { value: 'digitalizacao', label: 'Digitalização de original' },
              { value: 'contabilidade', label: 'Retorno da contabilidade' },
            ]}
            value={origin ?? 'manual'}
            onChange={(v) => setOrigin(v as UploadPayload['origin'])}
          />
        )}
      </div>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">Arquivo</span>
        <input
          type="file"
          accept={accept}
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setDuplicate(false); }}
          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-blue-700"
        />
      </label>
    </PessoasModal>
  );
}
